/*
 * LoudnessMeter — integrated loudness per ITU-R BS.1770-4 / EBU R128 (LUFS).
 *
 * Why this module exists (2026-08-14): /volume shipped buttons labelled
 * «YouTube (-14 LUFS)» and «Podcast (-16 LUFS)», and nothing anywhere measured
 * loudness. The preview normalised the PEAK to that number (so «-14 LUFS» put
 * the loudest sample at -14 dBFS — far quieter than intended), while the export
 * did something else again: dynaudnorm plus a flat -14 dB attenuation. Two
 * lanes, two behaviours, and neither was LUFS. A person heard one thing and
 * downloaded another.
 *
 * The fix is to make the promise true rather than to rename the buttons: the
 * targets are what people actually come for, and the measurement is a known,
 * testable algorithm.
 *
 *   LoudnessMeter.integrated(buffer) -> LUFS (number) | null when nothing
 *                                       passes the -70 LUFS absolute gate
 *
 * `buffer` is anything with { sampleRate, numberOfChannels, getChannelData(i) }
 * — an AudioBuffer in the browser, a plain object in the node tests.
 *
 * Method (BS.1770-4):
 *   1. K-weighting: a high-shelf stage then an RLB high-pass, both derived for
 *      the actual sample rate from the analogue prototype that reproduces the
 *      standard's published 48 kHz coefficients.
 *   2. Mean square per channel over 400 ms blocks overlapping by 75 %.
 *   3. Block loudness l = -0.691 + 10·log10(Σ G_i·z_i).
 *   4. Absolute gate at -70 LUFS, then a relative gate 10 LU below the
 *      absolutely-gated loudness. The gates are why silence between takes does
 *      not drag a podcast's number down.
 *
 * No DOM, no AudioContext — so the same file runs under `node --test`.
 */
(function (root) {
    'use strict';

    // Analogue prototype constants. At 48 kHz these reproduce the coefficients
    // printed in BS.1770-4 Tables 1 and 2 to the last decimal.
    var SHELF_F0 = 1681.974450955533;
    var SHELF_G  = 3.999843853973347;   // dB
    var SHELF_Q  = 0.7071752369554196;
    var HPF_F0   = 38.13547087602444;
    var HPF_Q    = 0.5003270373238773;

    var BLOCK_S   = 0.400;   // gating block length
    var OVERLAP   = 0.75;    // 75 % → a new block every 100 ms
    var ABS_GATE  = -70.0;   // LUFS
    var REL_GATE  = -10.0;   // LU below the absolutely-gated loudness
    var OFFSET    = -0.691;  // the standard's calibration term

    /**
     * High-shelf stage of the K-weighting filter.
     *
     * ⚠️ NOT the RBJ shelf: the standard's own design is used, the one
     * libebur128 implements. An RBJ shelf with the same f0/G/Q lands 0.25 LU
     * off — close enough to look right on a meter and wrong on every file.
     * Caught by the BS.1770 coefficient test below.
     */
    function shelfCoeffs(fs) {
        var K  = Math.tan(Math.PI * SHELF_F0 / fs);
        var Vh = Math.pow(10, SHELF_G / 20);
        var Vb = Math.pow(Vh, 0.4996667741545416);
        var a0 = 1 + K / SHELF_Q + K * K;

        return [
            (Vh + Vb * K / SHELF_Q + K * K) / a0,
            2 * (K * K - Vh) / a0,
            (Vh - Vb * K / SHELF_Q + K * K) / a0,
            2 * (K * K - 1) / a0,
            (1 - K / SHELF_Q + K * K) / a0,
        ];
    }

    /** RLB high-pass stage (numerator is exactly 1, -2, 1 by design). */
    function hpfCoeffs(fs) {
        var K  = Math.tan(Math.PI * HPF_F0 / fs);
        var a0 = 1 + K / HPF_Q + K * K;

        return [
            1, -2, 1,
            2 * (K * K - 1) / a0,
            (1 - K / HPF_Q + K * K) / a0,
        ];
    }

    /**
     * Sum of squares of the K-weighted channel, accumulated into 100 ms
     * sub-blocks.
     *
     * ⚠️ Streaming on purpose. The first version filtered the whole channel
     * into a Float64Array and built a prefix-sum array on top — three arrays
     * of frame length alive at once, about 700 MB for ten minutes of stereo.
     * That is the same shape as the crash /denoise hit on a 97-minute
     * recording: correct arithmetic, dead tab. Here the filter state is two
     * numbers and the only allocation is one entry per 100 ms.
     */
    function subBlockEnergy(src, shelf, hpf, sub) {
        var count = Math.floor(src.length / sub);
        var out = new Float64Array(count);

        var sb0 = shelf[0], sb1 = shelf[1], sb2 = shelf[2], sa1 = shelf[3], sa2 = shelf[4];
        var hb0 = hpf[0],   hb1 = hpf[1],   hb2 = hpf[2],   ha1 = hpf[3],   ha2 = hpf[4];
        var s1 = 0, s2 = 0, h1 = 0, h2 = 0;

        var idx = 0, acc = 0, n = 0;
        for (var i = 0; i < src.length; i++) {
            var x = src[i];

            var y1 = sb0 * x + s1;
            s1 = sb1 * x - sa1 * y1 + s2;
            s2 = sb2 * x - sa2 * y1;

            var y2 = hb0 * y1 + h1;
            h1 = hb1 * y1 - ha1 * y2 + h2;
            h2 = hb2 * y1 - ha2 * y2;

            acc += y2 * y2;
            if (++n === sub) {
                if (idx < count) out[idx++] = acc;
                acc = 0;
                n = 0;
            }
        }
        return out;
    }

    /**
     * Channel weights from BS.1770-4: front channels 1.0, surrounds 1.41,
     * LFE excluded. Anything up to stereo — which is nearly every file here —
     * is exactly the standard.
     */
    function weightFor(index, channels) {
        if (channels <= 2) {
            return 1.0;
        }
        if (channels >= 6) {
            // 5.1 layout L R C LFE Ls Rs
            if (index === 3) return 0;      // LFE never counts
            return index >= 4 ? 1.41 : 1.0;
        }
        return 1.0;
    }

    /**
     * @param {{sampleRate:number,numberOfChannels:number,getChannelData:function(number):Float32Array}} buffer
     * @returns {number|null} integrated loudness in LUFS, or null if the signal
     *          is silent / too short for a single 400 ms block.
     */
    function integrated(buffer) {
        if (!buffer || !buffer.sampleRate || !buffer.numberOfChannels) {
            return null;
        }
        var fs = buffer.sampleRate;
        var sub = Math.round(BLOCK_S * (1 - OVERLAP) * fs);   // 100 ms
        var perBlock = Math.round(1 / (1 - OVERLAP));         // 4 sub-blocks = 400 ms
        var frames = buffer.getChannelData(0).length;
        if (!sub || frames < sub * perBlock) {
            return null;
        }

        var shelf = shelfCoeffs(fs);
        var hpf = hpfCoeffs(fs);
        var channels = buffer.numberOfChannels;
        var subCount = Math.floor(frames / sub);
        var numBlocks = subCount - perBlock + 1;
        if (numBlocks < 1) {
            return null;
        }

        // Mean square per block per channel, built from the sub-block sums.
        var z = new Float64Array(numBlocks * channels);
        var blockLen = sub * perBlock;

        for (var ch = 0; ch < channels; ch++) {
            if (weightFor(ch, channels) === 0) {
                continue;
            }
            var e = subBlockEnergy(buffer.getChannelData(ch), shelf, hpf, sub);

            // Running window over 4 sub-blocks — no per-block re-summing.
            var window = 0, i;
            for (i = 0; i < perBlock; i++) {
                window += e[i];
            }
            z[0 * channels + ch] = window / blockLen;
            for (var b = 1; b < numBlocks; b++) {
                window += e[b + perBlock - 1] - e[b - 1];
                z[b * channels + ch] = window / blockLen;
            }
        }

        // Block loudness + absolute gate.
        var loud = new Float64Array(numBlocks);
        var keptAbs = [];
        for (var bb = 0; bb < numBlocks; bb++) {
            var sum = 0;
            for (var c2 = 0; c2 < channels; c2++) {
                sum += weightFor(c2, channels) * z[bb * channels + c2];
            }
            loud[bb] = sum > 0 ? OFFSET + 10 * Math.log10(sum) : -Infinity;
            if (loud[bb] > ABS_GATE) {
                keptAbs.push(bb);
            }
        }
        if (!keptAbs.length) {
            return null;
        }

        // Relative gate: 10 LU below the loudness of the absolutely-gated set.
        var meanZ = new Float64Array(channels);
        var k;
        for (k = 0; k < keptAbs.length; k++) {
            for (var c3 = 0; c3 < channels; c3++) {
                meanZ[c3] += z[keptAbs[k] * channels + c3];
            }
        }
        var sumAbs = 0;
        for (var c4 = 0; c4 < channels; c4++) {
            sumAbs += weightFor(c4, channels) * (meanZ[c4] / keptAbs.length);
        }
        if (!(sumAbs > 0)) {
            return null;
        }
        var relThreshold = OFFSET + 10 * Math.log10(sumAbs) + REL_GATE;

        var finalZ = new Float64Array(channels);
        var kept = 0;
        for (k = 0; k < keptAbs.length; k++) {
            var idx = keptAbs[k];
            if (loud[idx] <= relThreshold) {
                continue;
            }
            kept++;
            for (var c5 = 0; c5 < channels; c5++) {
                finalZ[c5] += z[idx * channels + c5];
            }
        }
        if (!kept) {
            return null;
        }

        var total = 0;
        for (var c6 = 0; c6 < channels; c6++) {
            total += weightFor(c6, channels) * (finalZ[c6] / kept);
        }

        return total > 0 ? OFFSET + 10 * Math.log10(total) : null;
    }

    var api = {
        integrated: integrated,
        // exported for the tests: the filter design is the part that silently rots
        shelfCoeffs: shelfCoeffs,
        hpfCoeffs: hpfCoeffs,
    };

    if (root) {
        root.LoudnessMeter = api;
    }
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = api;
    }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
